import { useEffect } from "react";
import { useConsent } from "@/lib/consent/ConsentProvider";

/**
 * Consent-gated loader for third-party tags.
 *
 * Nothing is injected until the visitor has made a choice in the cookie
 * banner: GA4 loads only with the "analytics" category, HubSpot tracking and
 * the LinkedIn Insight Tag only with the "marketing" category. Loading is
 * still deferred to the first interaction / idle moment so it never competes
 * with above-the-fold content.
 */

let analyticsLoaded = false;
let marketingLoaded = false;

function injectScript(opts: {
  src?: string;
  inline?: string;
  id?: string;
  async?: boolean;
  defer?: boolean;
}) {
  const s = document.createElement("script");
  if (opts.id) s.id = opts.id;
  if (opts.src) s.src = opts.src;
  if (opts.inline) s.text = opts.inline;
  if (opts.async) s.async = true;
  if (opts.defer) s.defer = true;
  document.head.appendChild(s);
}

function gtagUpdate(granted: { analytics: boolean; marketing: boolean }) {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  w.gtag?.("consent", "update", {
    analytics_storage: granted.analytics ? "granted" : "denied",
    ad_storage: granted.marketing ? "granted" : "denied",
    ad_user_data: granted.marketing ? "granted" : "denied",
    ad_personalization: granted.marketing ? "granted" : "denied",
  });
}

function loadAnalytics() {
  if (analyticsLoaded) return;
  analyticsLoaded = true;
  injectScript({ async: true, src: "https://www.googletagmanager.com/gtag/js?id=G-55XYQCNR5S" });
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  w.gtag?.("config", "G-55XYQCNR5S", { send_page_view: false });
}

function loadMarketing() {
  if (marketingLoaded) return;
  marketingLoaded = true;

  // LinkedIn Insight Tag
  injectScript({
    inline: `_linkedin_partner_id = "2005113";
window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
window._linkedin_data_partner_ids.push(_linkedin_partner_id);`,
  });
  injectScript({
    inline: `(function(l) {
if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
window.lintrk.q=[]}
var s = document.getElementsByTagName("script")[0];
var b = document.createElement("script");
b.type = "text/javascript";b.async = true;
b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
s.parentNode.insertBefore(b, s);})(window.lintrk);`,
  });

  // HubSpot tracking / forms loader
  injectScript({
    id: "hs-script-loader",
    async: true,
    defer: true,
    src: "https://js-eu1.hs-scripts.com/25788344.js",
  });
}

export function DeferredThirdParty() {
  const { consent } = useConsent();
  const analytics = !!consent?.categories.analytics;
  const marketing = !!consent?.categories.marketing;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!consent) return; // no decision yet - load nothing
    gtagUpdate({ analytics, marketing });
    if (!analytics && !marketing) return;
    if ((!analytics || analyticsLoaded) && (!marketing || marketingLoaded)) return;

    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    const opts: AddEventListenerOptions = { once: true, passive: true, capture: true };

    const fire = () => {
      cleanup();
      if (analytics) loadAnalytics();
      if (marketing) loadMarketing();
    };
    const cleanup = () => {
      for (const ev of events) window.removeEventListener(ev, fire, opts);
      window.clearTimeout(timer);
    };

    for (const ev of events) window.addEventListener(ev, fire, opts);
    const timer = window.setTimeout(fire, 1500);
    return cleanup;
  }, [consent, analytics, marketing]);

  return null;
}
